import { useCallback, useMemo, useState } from 'react';
import { AccountCircle, Dashboard, Logout } from '@mui/icons-material';
import { AppBar, Badge, Box, Divider, IconButton, ListItemIcon, Menu, MenuItem, Toolbar, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import squadGearIcon from '@assets/images/brand/squad-gear.svg';
import { useAppContext } from '@context/AppContext';
import { useAuth } from '@hooks/useAuth';
import { adminDashConfig, teamLockerConfig } from '@api/auth/config';
import { useLocation } from '@tanstack/react-router';
import { Helmet } from 'react-helmet-async';

const useStyles = makeStyles<{ needsMargin: boolean }>()((theme, { needsMargin }) => ({
  appBar: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    marginBottom: needsMargin ? theme.spacing(2) : 0,
  },
  logoSection: {
    display: 'flex',
    alignItems: 'center',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  content: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
    minWidth: '500px',
  },
  actionsSection: {
    marginLeft: 'auto',
  },
  mobileSteps: {
    display: 'none',
    width: '100%',
    gap: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      display: 'flex',
    },
  },
  stepBar: {
    flex: 1,
    height: 4,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: '#E5E7EB',
  },
  stepBarActive: {
    backgroundColor: '#3B82F6',
  },
}));

export function AppHeader() {
  const location = useLocation();
  const { classes } = useStyles({ needsMargin: location.pathname.startsWith('/signup') });
  // On the quote proposal pages the header stays at the top as the page scrolls.
  const isSticky = location.pathname.startsWith('/proposal');
  const { isAuthenticated, login } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { headerContent, menuIcons, selectedProductCount, currentStep, storeDetails, userData } = useAppContext();

  const storeName = useMemo(() => {
    return storeDetails?.name ?? '';
  }, [storeDetails]);

  const handleOpenMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleDashboardClick = useCallback(() => {
    window.location.href = `${adminDashConfig.baseUrl}/dashboard/stores`;
  }, []);

  const handleLogout = useCallback(() => {
    // Enter the cross-app logout chain at its head. It flows TeamLocker →
    // admin dash → back to this app's /oauth/logout page, which performs the
    // final DELETE /session and redirects to the post-logout landing page.
    window.location.href = `${teamLockerConfig.baseUrl}/#/logout`;
  }, []);

  const showAccountLinks = useMemo(() => {
    return Boolean(isAuthenticated && !storeDetails?.isQuickStart);
  }, [isAuthenticated, storeDetails?.isQuickStart]);

  return (
    <AppBar position={isSticky ? 'sticky' : 'static'} className={classes.appBar}>
      {storeName && (
        <Helmet>
          <title>{`${storeName} | Create Your Store | SquadLocker`}</title>
        </Helmet>
      )}
      <Toolbar>
        <Box className={classes.logoSection}>
          <a href='https://www.squadlocker.com/' target='_blank' rel='noopener noreferrer'>
            <img src={squadGearIcon} alt='Squad Gear Logo' style={{ height: 32 }} />
          </a>
        </Box>
        <Box className={classes.content}>{headerContent}</Box>
        <Box className={classes.actionsSection}>
          {menuIcons
            ?.sort((a, b) => a.order - b.order)
            .map(({ element, key, showBadgeCount }) => (
              <Badge key={key} overlap='circular' badgeContent={showBadgeCount ? selectedProductCount : undefined} color='primary'>
                {element}
              </Badge>
            ))}
          <IconButton disableRipple={true} onClick={handleOpenMenu} aria-label='Profile' size='large'>
            <AccountCircle fontSize='large' />
          </IconButton>
        </Box>
      </Toolbar>
      {headerContent && (
        <Box className={classes.mobileSteps}>
          {[1, 2, 3].map((step) => (
            <Box key={step} className={`${classes.stepBar} ${step <= currentStep ? classes.stepBarActive : ''}`} />
          ))}
        </Box>
      )}
      <Menu
        anchorEl={anchorEl}
        id='account-menu'
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            elevation: 0,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {showAccountLinks && (
          <Box sx={{ mx: 1, mb: 1 }}>
            <Typography variant='subtitle1'>
              {userData?.firstName} {userData?.lastName}
            </Typography>
            <Typography
              variant='body2'
              sx={{
                color: 'text.secondary',
              }}
            >
              {userData?.email}
            </Typography>
          </Box>
        )}
        {showAccountLinks && <Divider />}
        {showAccountLinks && (
          <MenuItem onClick={handleDashboardClick}>
            <ListItemIcon>
              <Dashboard fontSize='small' />
            </ListItemIcon>
            <Typography variant='body2'>Dashboard</Typography>
          </MenuItem>
        )}
        {showAccountLinks && (
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <Logout fontSize='small' />
            </ListItemIcon>
            <Typography variant='body2'>Logout</Typography>
          </MenuItem>
        )}
        {!isAuthenticated && (
          <MenuItem onClick={() => login()}>
            <Typography variant='body2'>Login</Typography>
          </MenuItem>
        )}
      </Menu>
    </AppBar>
  );
}
